import { FC, useCallback, useMemo, useState } from 'react';
import { formatDate, formatPrice } from '@utilities/functions';
import { CreditDataType, CreditType } from '@utilities/types';
import { Button, Input, InputWrapper, Modal } from '@components';
import { OrderDetails } from './orderDetails';
import { CreditPaymentsHistory } from './CreditPaymentsHistory';
import { MobilePaymentEnum, PaymentMethodeEnum } from '@utilities/enums';
import { Banknote, CreditCard as CreditCardIcon, FileText, Smartphone, Wallet } from 'lucide-react';

interface CreditCardProps {
    credit: CreditType;
    onPayCredit: (creditId: string, creditData: CreditDataType) => void;
    isLoading?: boolean;
}

const getPaymentIcon = (methode: PaymentMethodeEnum) => {
    switch (methode) {
        case PaymentMethodeEnum.esp:
            return <Banknote className="w-4 h-4" />;
        case PaymentMethodeEnum.mobilMoney:
            return <Smartphone className="w-4 h-4" />;
        case PaymentMethodeEnum.cb:
            return <CreditCardIcon className="w-4 h-4" />;
        case PaymentMethodeEnum.cheque:
            return <FileText className="w-4 h-4" />;
        default:
            return <Wallet className="w-4 h-4" />;
    }
};

export const CreditCard: FC<CreditCardProps> = ({
    credit,
    onPayCredit,
    isLoading = false,
}) => {

    const { _clientID: client, remainingToBePaid, amount, _saleID: sale } = credit;

    const [displayDetailsOrder, setDisplayDetailsOrder] = useState<boolean>(false);
    const [displayHistory, setDisplayHistory] = useState<boolean>(false);
    const [displayCreditPayment, setDisplayCreditPayment] = useState<boolean>(false);

    // États du formulaire de paiement
    const [amountToPay, setAmountToPay] = useState<number>(remainingToBePaid || 0);
    const [selectedMethode, setSelectedMethode] = useState<PaymentMethodeEnum>(PaymentMethodeEnum.esp);
    const [selectedMobileMode, setSelectedMobileMode] = useState<MobilePaymentEnum | null>(null);
    const [transactionRef, setTransactionRef] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    // Filtrer les méthodes de paiement (pas de crédit pour rembourser un crédit)
    const availablePaymentMethods = useMemo(() =>
        Object.entries(PaymentMethodeEnum).filter(([_, value]) =>
            value !== PaymentMethodeEnum.credit
        ),
        []);

    const mobileModes = useMemo(() => Object.entries(MobilePaymentEnum), []);

    const isMobilePayment = useMemo(() =>
        selectedMethode === PaymentMethodeEnum.mobilMoney,
        [selectedMethode]);

    const canPay = useMemo(() => {
        if (amountToPay <= 0 || amountToPay > remainingToBePaid) return false;
        if (isMobilePayment && !selectedMobileMode) return false;
        return true;
    }, [amountToPay, remainingToBePaid, isMobilePayment, selectedMobileMode]);

    const onToggleDetailsOrder = useCallback(() => {
        setDisplayDetailsOrder((prev) => !prev);
    }, []);

    const onToggleHistory = useCallback(() => {
        setDisplayHistory((prev) => !prev);
    }, []);

    const onToggleCreditPayment = useCallback(() => {
        setDisplayCreditPayment((prev) => !prev);
        // Réinitialiser le formulaire à l'ouverture
        if (!displayCreditPayment) {
            setAmountToPay(remainingToBePaid || 0);
            setSelectedMethode(PaymentMethodeEnum.esp);
            setSelectedMobileMode(null);
            setTransactionRef('');
            setNotes('');
        }
    }, [displayCreditPayment, remainingToBePaid]);

    const handleCreditPayment = useCallback(() => {
        if (!canPay) return;

        const creditData: CreditDataType = {
            amount: amountToPay,
            methode: selectedMethode,
            ...(isMobilePayment && selectedMobileMode && { mobilePayment: selectedMobileMode }),
            ...(transactionRef && { transactionRef }),
            ...(notes && { notes }),
        };

        onPayCredit(credit._id, creditData);
        onToggleCreditPayment();
    }, [
        canPay, amountToPay, selectedMethode, isMobilePayment,
        selectedMobileMode, transactionRef, notes,
        credit._id, onPayCredit, onToggleCreditPayment,
    ]);

    const fillRemainingAmount = useCallback(() => {
        setAmountToPay(remainingToBePaid);
    }, [remainingToBePaid]);

    const renderMethodeCard = useCallback((
        label: string,
        isSelected: boolean,
        onClick: () => void,
        methode: PaymentMethodeEnum,
    ) => (
        <div
            key={label}
            onClick={onClick}
            className={`cursor-pointer border rounded-lg px-3 py-2 text-xs font-medium transition-all flex items-center gap-2
                ${isSelected ? 'bg-primary text-white border-primary' : 'bg-white hover:bg-gray-100 text-gray-600 border-gray-200'}`}
        >
            {getPaymentIcon(methode)}
            {label}
        </div>
    ), []);

    const renderMobileModeCard = useCallback((
        label: string,
        isSelected: boolean,
        onClick: () => void,
    ) => (
        <div
            key={label}
            onClick={onClick}
            className={`cursor-pointer border rounded-lg px-3 py-2 text-xs font-medium transition-all
                ${isSelected ? 'bg-orange-500 text-white border-orange-500' : 'bg-white hover:bg-gray-100 text-gray-600 border-gray-200'}`}
        >
            {label}
        </div>
    ), []);

    return (
        <>
            <div className="bg-white shadow-md rounded-lg p-4 space-y-3 border">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-primary">
                        {client.firstname} {client.lastname}
                    </h3>
                    <p className="text-sm text-gray-500">Téléphone : {client.phoneNumber}</p>
                </div>

                <div className="text-sm text-gray-700">
                    <p><strong>Montant total :</strong> {formatPrice(amount)}</p>
                    <p><strong>Restant dû :</strong> <span className="text-red-600 font-semibold">{formatPrice(remainingToBePaid)}</span></p>
                    <p><strong>Commande :</strong> {sale.orderNumber}</p>
                    <p><strong>Date :</strong> {formatDate(sale.createAt)}</p>
                </div>

                {/* Barre de progression */}
                <div className="pt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progression du remboursement</span>
                        <span>{Math.round(((amount - remainingToBePaid) / amount) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((amount - remainingToBePaid) / amount) * 100}%` }}
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                    <Button variant="success" onClick={onToggleCreditPayment}>
                        Payer le crédit
                    </Button>

                    <Button variant="info" onClick={onToggleDetailsOrder}>
                        Voir les détails
                    </Button>

                    <Button variant="secondary" onClick={onToggleHistory}>
                        Historique ({credit.payments.length})
                    </Button>
                </div>
            </div>

            <Modal
                isOpen={displayDetailsOrder}
                onClose={onToggleDetailsOrder}
                title={`Détails de la commande ${sale.orderNumber}`}
            >
                <OrderDetails
                    client={client}
                    sale={sale}
                    credit={credit}
                />
            </Modal>

            <Modal
                isOpen={displayHistory}
                onClose={onToggleHistory}
                title={"Historique des paiements du crédit"}
            >
                <CreditPaymentsHistory
                    credit={credit}
                />
            </Modal>

            <Modal
                isOpen={displayCreditPayment}
                onClose={onToggleCreditPayment}
                title="Paiement du crédit"
            >
                <div className="space-y-5">
                    {/* Résumé */}
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600 font-medium">Montant restant à payer</span>
                            <span className="text-xl font-bold text-red-600">{formatPrice(remainingToBePaid)}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Client: {client.firstname} {client.lastname}
                        </p>
                    </div>

                    {/* Montant à payer */}
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <InputWrapper label='Montant à payer'>
                                <Input
                                    type='number'
                                    placeholder='ex: 1000'
                                    min={0}
                                    max={remainingToBePaid}
                                    value={amountToPay || ''}
                                    onChange={(e) => setAmountToPay(Number(e.target.value))}
                                />
                            </InputWrapper>
                        </div>
                        <Button
                            variant="info"
                            className="text-xs px-3 py-2 h-[42px]"
                            onClick={fillRemainingAmount}
                        >
                            Tout payer
                        </Button>
                    </div>

                    {/* Sélection méthode de paiement */}
                    <div>
                        <p className="text-sm text-gray-600 mb-2 font-medium">Méthode de paiement</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {availablePaymentMethods.map(([key, value]) =>
                                renderMethodeCard(
                                    value,
                                    selectedMethode === value,
                                    () => {
                                        setSelectedMethode(value as PaymentMethodeEnum);
                                        setSelectedMobileMode(null);
                                    },
                                    value as PaymentMethodeEnum
                                )
                            )}
                        </div>
                    </div>

                    {/* Sélection opérateur mobile */}
                    {isMobilePayment && (
                        <div>
                            <p className="text-sm text-gray-600 mb-2 font-medium">Opérateur mobile</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {mobileModes.map(([key, value]) =>
                                    renderMobileModeCard(
                                        value,
                                        selectedMobileMode === value,
                                        () => setSelectedMobileMode(value as MobilePaymentEnum)
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Référence de transaction */}
                    {(isMobilePayment || selectedMethode === PaymentMethodeEnum.cheque || selectedMethode === PaymentMethodeEnum.cb) && (
                        <InputWrapper label="Référence de transaction (optionnel)">
                            <Input
                                type="text"
                                placeholder="ex: TXN123456"
                                value={transactionRef}
                                onChange={(e) => setTransactionRef(e.target.value)}
                            />
                        </InputWrapper>
                    )}

                    {/* Notes */}
                    <InputWrapper label="Notes (optionnel)">
                        <Input
                            type="text"
                            placeholder="ex: Paiement partiel, reste à payer le mois prochain"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </InputWrapper>

                    {/* Bouton de validation */}
                    <div className="flex justify-end pt-2">
                        <Button
                            variant="success"
                            onClick={handleCreditPayment}
                            disabled={!canPay || isLoading}
                            className="min-w-[150px]"
                        >
                            {isLoading ? 'Traitement...' : `Payer ${formatPrice(amountToPay)}`}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
